import qs from 'qs'

import HttpClient from 'services/HttpClient'
import { IQuery } from 'types/slices'

export class AssetsRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  // Assets
  getOwnershipLabels({ query }: { query: IQuery }) {
    return this.api.get(`/assets/ownership-labels?${qs.stringify(query)}`)
  }

  getAssets({ query }: { query: IQuery }) {
    const initialQuery = {
      order: {
        by: 'createdAt',
        dir: 'desc'
      },
      limit: { take: 8, page: 1 },
      filter: { country: 'PL' }
    }
    const params = { ...initialQuery, ...query }

    return this.api.get(`/assets?${qs.stringify(params)}`)
  }

  getBrandsAssets() {
    return this.api.get('/brands/all')
  }

  getAssetsTypesByProductCategoryId(id: string) {
    return this.api.get(`/asset-types/all?filter[productCategory][id]=${id}`)
  }

  getAssetTypeById(id: string) {
    return this.api.get(`/asset-types/${id}`)
  }

  createAssetType(payload: {
    name: string
    productCategoryId: string
    maxNumberPerProduct?: number
    subtypes?: string[]
  }) {
    return this.api.post('/asset-types', payload)
  }

  getAssetSubtypesByAssetTypeId(assetTypeId: string) {
    return this.api.get(`/asset-types/${assetTypeId}/subtypes`)
  }

  getAssetSubtypesAll(assetTypeId?: string) {
    const query = assetTypeId
      ? `?filter[assetType][id]=${assetTypeId}`
      : ''

    return this.api.get(`/asset-subtypes/all${query}`)
  }

  createAssetSubtype(payload: { name: string; assetTypeId: string }) {
    return this.api.post('/asset-subtypes', payload)
  }

  updateAssetSubtype(
    id: string,
    payload: { name?: string; orderInAssetType?: number }
  ) {
    return this.api.patch(`/asset-subtypes/${id}`, payload)
  }

  deleteAssetSubtype(id: string) {
    return this.api.delete(`/asset-subtypes/${id}`)
  }

  updateAssetType(
    id: string,
    payload: { name?: string; maxNumberPerProduct?: number }
  ) {
    return this.api.patch(`/asset-types/${id}`, payload)
  }

  deleteAssetType(id: string) {
    return this.api.delete(`/asset-types/${id}`)
  }

  getAssetsAll(params?: { assetTypeId?: string }) {
    const query = params?.assetTypeId
      ? { filter: { assetType: { id: params.assetTypeId } } }
      : {}

    return this.api.get(`/assets/all?${qs.stringify(query)}`)
  }

  getAssetById(id: string) {
    return this.api.get(`/assets/${id}`)
  }

  createAsset(formData: FormData) {
    return this.api.post(`/assets/form`, formData)
  }

  copyAsset(id: string) {
    return this.api.post(`/assets/${id}/clone`)
  }


  editAsset(id: string, formData: FormData) {
    return this.api.patch(`/assets/${id}/form`, formData)
  }

  getAssetUsages(id: string) {
    return this.api.get(`/assets/${id}/usages`)
  }

  saveAssetWithScope(
    id: string,
    formData: FormData,
    scope: { mode: 'all' | 'selected'; productIds?: string[] }
  ) {
    const params = new URLSearchParams()

    params.set('mode', scope.mode)
    if (scope.mode === 'selected' && scope.productIds?.length) {
      params.set('productIds', JSON.stringify(scope.productIds))
    }
    formData.delete('mode')
    formData.delete('productIds')

    return this.api.postFormData(
      `/assets/${id}/save-with-scope?${params.toString()}`,
      formData
    )
  }

  setAssetBlocked(id: string, isBlocked: boolean) {
    return this.api.patch(`/assets/${id}/blocked`, { isBlocked })
  }

  assignAssetAllowedEditor(id: string, email: string) {
    return this.api.patch(`/assets/${id}/allowed-editor`, { email })
  }

  acceptDraftAsset(id: string, userId: string) {
    return this.api.put(`/ai-translations/assets/${id}/accept-translation`, {
      acceptedByUserId: userId
    })
  }

  deleteAsset(id: string) {
    return this.api.delete(`/assets/${id}`)
  }

  getProductsCategoriesNames() {
    return this.api.get(`/product-categories/all/names`)
  }

  getProductsCategoriesAssets(id: string, country: string) {
    return this.api.get(`/product-categories/${id}/${country}/assets`)
  }
}

 
export default new AssetsRepository(HttpClient)
